import {computed, ref} from 'vue'
import {defineStore} from 'pinia'
import axios from "axios";
import {GermanCountryCode, GuestAppApiUrl} from "@/constants";
import {useGuestAuthStore} from "@/stores/guestAuth";
import {useBookingStore} from "@/stores/booking";
import {DateTime} from "luxon";

const guestDefaultObj = {
    position: '',
    firstName: '',
    lastName: '',
    birthday: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    documentType: '',
    documentNumber: '',
    nationality: '',
    purposeOfStay: '',
    // Zwischenstand: gespeichert, aber noch nicht vollständig gemeldet. Der
    // Server führt dasselbe Feld und vergibt für Entwürfe keinen hash.
    draft: false,
    hash: ''
}

// The API may return no birthday at all; formatting that unguarded writes the
// string "Invalid DateTime" into the record and it gets posted back.
const toDateInputValue = value => {
    const date = DateTime.fromISO(value ?? '')

    // Ein Zwischenstand ohne Geburtsdatum kommt als Null-Zeit zurück
    // (0001-01-01). Als Wert eines <input type="date"> liegt das unter dem
    // erlaubten Mindestdatum — das Feld gilt dann als ungültig und der Gast
    // bekommt das Formular nie mehr abgesendet.
    if (!date.isValid || date.year < 1900) {
        return ''
    }

    return date.toFormat('yyyy-MM-dd')
}

const isAbroad = country => !!country && country !== GermanCountryCode

const initGuests = amount => {
    const buffer = [];

    for (let i = 0; i < amount; i++) {
        buffer[i] = {
            position: i,
            ...guestDefaultObj,
        }
    }

    return buffer
};

export const useGuestsStore = defineStore('guests', () => {
    const bookingStore = useBookingStore()
    const guestAuth = useGuestAuthStore()
    const guests = ref(initGuests(bookingStore.booking.guests.totalAmount))
    const addons = ref({})

    // Which guests live outside Germany. UI state only — it drives which fields
    // the check-in asks for and is never part of the guest payload.
    const livesAbroad = ref(guests.value.map(() => false))

    const guestsValidAmount = computed(
        () => guests.value.filter(guest => guest.hash).length
    )

    function setLivesAbroad(index, value) {
        livesAbroad.value[index] = value
    }

    function fetchGuests() {
        return axios.get(
            '/v1/booking/guests',
            {
                baseURL: GuestAppApiUrl,
                headers: {
                    "X-API-Key": guestAuth.token,
                }
            }
        )
            .then(({data}) => {
                if (data.length === 0) {
                    return
                }

                guests.value = guests.value
                    .map(
                        (guest, index) => {
                            const savedGuest = data.find(sg => sg.position === index)
                            if (savedGuest) {
                                savedGuest.birthday = toDateInputValue(savedGuest.birthday)

                                // Only a stored record tells us where the guest
                                // lives; an untouched slot keeps whatever the
                                // guest has already picked in the open form.
                                livesAbroad.value[index] = isAbroad(savedGuest.country)

                                return savedGuest
                            }

                            return guest
                        }
                    );
            })
    }

    function postGuest(guest, index, draft) {
        return axios.post(
            '/v1/booking/guests',
            {
                ...guest,
                index: index,
                // Immer explizit: `guest` trägt das Feld aus einem früheren
                // Entwurf mit sich, und ein fertiger Datensatz darf davon
                // nicht als Zwischenstand gespeichert werden.
                draft: draft,
            },
            {
                baseURL: GuestAppApiUrl,
                headers: {
                    "X-API-Key": guestAuth.token,
                }
            }
        )
    }

    function saveGuest(guest, index) {
        return postGuest(guest, index, false)
            .then(({data}) => {
                guests.value[index].hash = data.hash
                guests.value[index].draft = false
            })
    }

    // Sichert stillschweigend, was schon dasteht — ohne Pflichtfelder zu
    // verlangen. Damit können Mitreisende ihre Angaben selbst und zu
    // verschiedenen Zeiten eintragen, ohne dass ein halb ausgefülltes
    // Formular verlorengeht.
    function saveDraft(index) {
        const guest = guests.value[index]

        if (guest.hash) {
            return Promise.resolve()
        }

        return postGuest(guest, index, true)
            .then(({data}) => {
                // Entwurf ist nur, was ohne hash dasteht. Kommt die Antwort
                // erst nach der des endgültigen Speicherns, trägt der Gast
                // schon seinen hash; der Server hat den Entwurf dann verworfen
                // und den hash des gemeldeten Gasts zurückgeschickt
                // (Draft-Guard). Gesetzt stünde das Flag an einem fertigen
                // Gast.
                if (!guests.value[index].hash && !data?.hash) {
                    guests.value[index].draft = true
                }
            })
    }

    function checkIn() {
        return axios.post(
            '/v1/booking/checkin',
            {addons: bookingStore.booking.addons},
            {
                baseURL: GuestAppApiUrl,
                headers: {
                    "X-API-Key": guestAuth.token,
                }
            }
        )
            .then(() => bookingStore.booking.checkedIn = true)
    }

    return {guests, guestsValidAmount, addons, livesAbroad, setLivesAbroad, fetchGuests, saveGuest, saveDraft, checkIn}
})
